/**
 * Info-file generator for Anomalieerkennung downloads.
 *
 * Matches the W6/W7/W8 convention: every saved data CSV gets a sibling
 * `${baseFileName}_info.txt` documenting which params and which thresholds
 * were used to produce it. Lets the user audit / reproduce a run later.
 *
 * Threshold values (STL/LSTM) are NOT currently tracked in the FSM state,
 * so they're omitted from the info file. If we later persist them in the
 * reducer (e.g. via APPLYING_STL action payload), add them here.
 */
import type { AnomalyState } from './anomalyReducer';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export function generateAnomalyInfoFile(state: AnomalyState, t: TFn): string {
  const now = new Date();
  const timestamp = now.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const lines: string[] = [];
  const sep = '='.repeat(60);
  const subsep = '-'.repeat(40);

  lines.push(t('infoFile.header', { defaultValue: 'Anomaly Detection - Info File' }));
  lines.push(sep);
  lines.push('');
  lines.push(`${t('infoFile.generatedAt', { defaultValue: 'Generated at' })}: ${timestamp}`);
  lines.push('');

  // Source file
  if (state.filename) {
    lines.push(`${t('infoFile.uploadedFile', { defaultValue: 'Uploaded file' })}: ${state.filename}`);
  }
  if (state.processedCsvFilename) {
    lines.push(`${t('infoFile.processedFile', { defaultValue: 'Processed file' })}: ${state.processedCsvFilename}`);
  }
  if (state.columnName) {
    lines.push(`${t('infoFile.columnName', { defaultValue: 'Column' })}: ${state.columnName}`);
  }
  if (state.rowCount) {
    lines.push(`${t('infoFile.rowCount', { defaultValue: 'Rows' })}: ${state.rowCount}`);
  }
  lines.push('');

  // Parameters
  const { params } = state;
  lines.push(`${t('infoFile.parameters', { defaultValue: 'Processing parameters' })}:`);
  lines.push(subsep);

  const fmt = (key: string, fallback: string, value: string) => {
    if (value === '' || value == null) return;
    lines.push(`  ${t(key, { defaultValue: fallback })}: ${value}`);
  };

  fmt('infoFile.eqMax', 'Constant-value duration max (eq_max) [min]', params.eqMax);
  fmt('infoFile.gapMax', 'Max gap (gap_max) [min]', params.gapMax);
  fmt('infoFile.dec', 'Decimal places (dec)', params.dec);
  fmt('infoFile.lgMin', 'Min valid-segment length (lg_min) [min]', params.lgMin);
  fmt('infoFile.vMax', 'Max allowed value (v_max)', params.vMax);
  fmt('infoFile.vMin', 'Min allowed value (v_min)', params.vMin);

  const yesNo = (b: boolean) =>
    b ? t('infoFile.ja', { defaultValue: 'Yes' }) : t('infoFile.nein', { defaultValue: 'No' });

  lines.push(`  ${t('infoFile.valueNullHandling', { defaultValue: 'Null-value handling (el0)' })}: ${yesNo(params.el0)}`);
  lines.push('');

  // SBAD slope detection
  if (params.sbad.chgMax !== '' || params.sbad.lgMax !== '') {
    lines.push(`${t('infoFile.sbadSection', { defaultValue: 'Slope-based anomaly detection (SBAD)' })}:`);
    lines.push(subsep);
    fmt('infoFile.chgMax', 'Slope threshold (chg_max) [#/min]', params.sbad.chgMax);
    fmt('infoFile.lgMax', 'Anomaly duration threshold (lg_max) [min]', params.sbad.lgMax);
    lines.push('');
  }

  // STL
  lines.push(`${t('infoFile.stlSection', { defaultValue: 'STL anomaly detection' })}:`);
  lines.push(subsep);
  lines.push(`  ${t('infoFile.stlEnabled', { defaultValue: 'Enabled' })}: ${yesNo(params.stl.run)}`);
  if (params.stl.run) {
    fmt('infoFile.periodH', 'Period [h]', params.stl.periodH);
  }
  lines.push('');

  // LSTM
  lines.push(`${t('infoFile.lstmSection', { defaultValue: 'LSTM anomaly detection' })}:`);
  lines.push(subsep);
  lines.push(`  ${t('infoFile.lstmEnabled', { defaultValue: 'Enabled' })}: ${yesNo(params.lstm.run)}`);
  if (params.lstm.run) {
    fmt('infoFile.periodH', 'Period [h]', params.lstm.periodH);
    fmt('infoFile.neurons', 'Neurons', params.lstm.neurons);
    fmt('infoFile.epochs', 'Epochs', params.lstm.epochs);
    fmt('infoFile.batchSize', 'Batch size', params.lstm.batchSize);
  }
  lines.push('');
  lines.push(sep);

  return lines.join('\n');
}

/**
 * Trigger a browser download of an info file alongside a processed CSV.
 * `csvFilename` is e.g. "a1-5min-interval_1.csv" → info becomes
 * "a1-5min-interval_1_info.txt". Matches W6/W7/W8 convention.
 */
export function downloadAnomalyInfoFile(state: AnomalyState, csvFilename: string, t: TFn): void {
  try {
    const content = generateAnomalyInfoFile(state, t);
    const baseFileName = csvFilename.replace(/\.csv$/i, '');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseFileName}_info.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    // Supplementary file — never block the main CSV download flow.
    // Match W8 useAdjustmentsSave.downloadInfoFile semantic.
    // eslint-disable-next-line no-console
    console.error('Error downloading anomaly info file:', err);
  }
}
